import { inject, Injectable } from "@angular/core";
import { Observable, of } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { PolicyCacheDto } from "../models/policy-cache.model";
import { SessionCacheApiService } from "./session-cache-api.service";
import { LoginDetailsStorageService } from "./login-details-storage.service";

/** App sessionStorage keys bridged for policy cache (not stored in browser). */
export const POLICY_DETAILS_STORAGE_KEYS = new Set([
  "policyId",
  "policyNo",
  "policyDate",
  "policyStatus",
  "policyAddressId",
  "quoteIdForPolicy",
]);

const CACHE_TYPE = "policy";

@Injectable({
  providedIn: "root",
})
export class PolicyDetailsStorageService {
  private readonly sessionCacheApi = inject(SessionCacheApiService);
  private readonly loginDetailsStorage = inject(LoginDetailsStorageService);
  private readonly values = new Map<string, string>();
  private static bridgeInstalled = false;
  private persistTimer: ReturnType<typeof setTimeout> | null = null;
  private persistEnabled = true;
  private loaded = false;

  purgeNativePolicyKeysFromBrowser(): void {
    if (typeof window === "undefined") {
      return;
    }
    const nativeRemove = Storage.prototype.removeItem;
    POLICY_DETAILS_STORAGE_KEYS.forEach((key) => {
      try {
        nativeRemove.call(window.sessionStorage, key);
      } catch {
        /* ignore */
      }
    });
  }

  /** Chain after QuoteDetailsStorageService.initSessionStorageBridge(). */
  initSessionStorageBridge(): void {
    if (PolicyDetailsStorageService.bridgeInstalled || typeof window === "undefined") {
      return;
    }

    const storageProto = Object.getPrototypeOf(window.sessionStorage) as Storage;
    const wrappedGetItem = storageProto.getItem;
    const wrappedSetItem = storageProto.setItem;
    const wrappedRemoveItem = storageProto.removeItem;
    const store = this;

    storageProto.getItem = function (key: string): string | null {
      if (this === window.sessionStorage && POLICY_DETAILS_STORAGE_KEYS.has(key)) {
        return store.getItem(key);
      }
      return wrappedGetItem.call(this, key);
    };

    storageProto.setItem = function (key: string, value: string): void {
      if (this === window.sessionStorage && POLICY_DETAILS_STORAGE_KEYS.has(key)) {
        store.setItem(key, value);
        return;
      }
      wrappedSetItem.call(this, key, value);
    };

    storageProto.removeItem = function (key: string): void {
      if (this === window.sessionStorage && POLICY_DETAILS_STORAGE_KEYS.has(key)) {
        store.removeItem(key);
        return;
      }
      wrappedRemoveItem.call(this, key);
    };

    PolicyDetailsStorageService.bridgeInstalled = true;
  }

  getItem(key: string): string | null {
    if (!POLICY_DETAILS_STORAGE_KEYS.has(key)) {
      return null;
    }
    return this.values.get(key) ?? null;
  }

  setItem(key: string, value: string): void {
    if (!POLICY_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.set(key, String(value));
    this.schedulePersist();
  }

  removeItem(key: string): void {
    if (!POLICY_DETAILS_STORAGE_KEYS.has(key)) {
      return;
    }
    this.values.delete(key);
    this.schedulePersist();
  }

  clear(): void {
    this.values.clear();
    this.loaded = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  disablePersist(): void {
    this.persistEnabled = false;
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
      this.persistTimer = null;
    }
  }

  enablePersist(): void {
    this.persistEnabled = true;
  }

  isLoaded(): boolean {
    return this.loaded;
  }

  getCacheSessionId(): string | null {
    return this.loginDetailsStorage.getCacheSessionId();
  }

  buildCachePayload(overrides: Partial<PolicyCacheDto> = {}): PolicyCacheDto {
    const payload: PolicyCacheDto = {
      policyId: this.toCacheInt(this.getItem("policyId")),
      policyNo: this.getItem("policyNo") ?? "",
      policyDate: this.toCacheDateOrNull(this.getItem("policyDate")),
      policyStatus: this.getItem("policyStatus") ?? "",
      policyAddressId: this.toCacheAddressId(this.getItem("policyAddressId")),
      quoteIdForPolicy: this.toCacheInt(this.getItem("quoteIdForPolicy")),
      policyAddressTypeId: this.toCacheAddressId(this.readSessionField("addressTypeId")),
      ...overrides,
    };

    return {
      policyId: this.toCacheInt(payload.policyId),
      policyNo: payload.policyNo ?? "",
      policyDate: this.toCacheDateOrNull(
        payload.policyDate != null ? String(payload.policyDate) : null
      ),
      policyStatus: payload.policyStatus ?? "",
      policyAddressId: this.toCacheInt(payload.policyAddressId),
      quoteIdForPolicy: this.toCacheInt(payload.quoteIdForPolicy),
      policyAddressTypeId: this.toCacheInt(payload.policyAddressTypeId),
    };
  }

  applyFromCacheDto(data: PolicyCacheDto): void {
    if (data.policyId !== undefined && data.policyId !== null && data.policyId !== 0) {
      this.values.set("policyId", String(data.policyId));
    }
    if (data.policyNo) {
      this.values.set("policyNo", String(data.policyNo));
    }
    if (data.policyDate !== undefined && data.policyDate !== null && String(data.policyDate).trim() !== "") {
      this.values.set("policyDate", String(data.policyDate));
    } else if (data.policyDate === null) {
      this.values.delete("policyDate");
    }
    if (data.policyStatus) {
      this.values.set("policyStatus", String(data.policyStatus));
    }
    if (data.policyAddressId !== undefined && data.policyAddressId !== null) {
      this.values.set(
        "policyAddressId",
        this.formatAddressIdForSession(data.policyAddressId)
      );
    }
    if (data.quoteIdForPolicy !== undefined && data.quoteIdForPolicy !== null && data.quoteIdForPolicy !== 0) {
      this.values.set("quoteIdForPolicy", String(data.quoteIdForPolicy));
    }
    if (
      data.policyAddressTypeId !== undefined &&
      data.policyAddressTypeId !== null &&
      data.policyAddressTypeId !== 0
    ) {
      this.writeSessionField(
        "addressTypeId",
        this.formatAddressIdForSession(data.policyAddressTypeId)
      );
    }

    this.loaded = true;
  }

  loadFromCache(sessionId?: string | number): Observable<boolean> {
    const token = this.loginDetailsStorage.getItem("token");
    const sid = sessionId ?? this.getCacheSessionId();

    if (!token || !sid) {
      return of(false);
    }

    return this.sessionCacheApi.get<PolicyCacheDto>(CACHE_TYPE, sid).pipe(
      tap((data) => {
        if (data) {
          this.applyFromCacheDto(data);
        }
      }),
      map((data) => !!data),
      catchError(() => of(false))
    );
  }

  saveToCache(
    overrides: Partial<PolicyCacheDto> = {},
    sessionIdOverride?: string | number
  ): Observable<PolicyCacheDto | null> {
    const sessionId = sessionIdOverride ?? this.getCacheSessionId();
    if (!sessionId) {
      return of(null);
    }

    const payload = this.buildCachePayload(overrides);
    this.applyFromCacheDto(payload);

    return this.sessionCacheApi.set(CACHE_TYPE, sessionId, payload).pipe(
      map(() => payload),
      catchError(() => of(payload))
    );
  }

  removeFromCache(sessionId?: string | number): Observable<unknown> {
    const sid = sessionId ?? this.getCacheSessionId();
    if (!sid) {
      return of(null);
    }
    return this.sessionCacheApi.remove(CACHE_TYPE, sid);
  }

  private toCacheDateOrNull(value: string | null | undefined): string | null {
    if (value === null || value === undefined) {
      return null;
    }
    const trimmed = String(value).trim();
    if (trimmed === "" || trimmed === "null" || trimmed === "undefined") {
      return null;
    }
    return trimmed;
  }

  toCacheInt(value: string | number | null | undefined): number {
    if (value === null || value === undefined || value === "") {
      return 0;
    }
    const parsed = typeof value === "number" ? value : Number(value);
    return Number.isFinite(parsed) ? Math.trunc(parsed) : 0;
  }

  private readSessionField(key: string): string | null {
    if (typeof window === "undefined") {
      return null;
    }
    return window.sessionStorage.getItem(key);
  }

  private writeSessionField(key: string, value: string): void {
    if (typeof window === "undefined") {
      return;
    }
    window.sessionStorage.setItem(key, value);
  }

  private toCacheAddressId(value: string | null): number {
    if (!value) {
      return 0;
    }
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return this.toCacheInt(parsed[0]);
      }
      return this.toCacheInt(parsed);
    } catch {
      return this.toCacheInt(value);
    }
  }

  private formatAddressIdForSession(addressId: number): string {
    if (!addressId) {
      return "";
    }
    const existing = this.getItem("policyAddressId");
    if (existing?.startsWith("[")) {
      return existing;
    }
    return JSON.stringify([addressId]);
  }

  private schedulePersist(): void {
    if (!this.persistEnabled) {
      return;
    }
    if (this.persistTimer) {
      clearTimeout(this.persistTimer);
    }
    this.persistTimer = setTimeout(() => {
      this.persistTimer = null;
      const sessionId = this.getCacheSessionId();
      if (sessionId) {
        this.saveToCache().subscribe();
      }
    }, 300);
  }
}
